/**
 * Zustand store for postprocessing effect settings.
 * All effects are configurable in real time via the DESIGN tab in settings panel.
 * Settings can be exported/imported as JSON for sharing presets.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ---- Tone mapping mode enum (mirrors postprocessing ToneMappingMode) ----

export const ToneMappingMode = {
  LINEAR: 0,
  REINHARD: 1,
  UNCHARTED2: 2,
  ACES_FILMIC: 4,
} as const;

export type ToneMappingModeValue = (typeof ToneMappingMode)[keyof typeof ToneMappingMode];

// ---- Per-effect settings types ----

export interface BloomSettings {
  enabled: boolean;
  intensity: number;        // 0-20
  luminanceThreshold: number; // 0-1
  luminanceSmoothing: number; // 0-1
  mipmapBlur: boolean;
  radius: number;           // 0-1
}

export interface VignetteSettings {
  enabled: boolean;
  darkness: number;  // 0-1
  offset: number;    // 0-1
}

export interface ChromaticAberrationSettings {
  enabled: boolean;
  offsetX: number;          // 0-0.1
  offsetY: number;          // 0-0.1
  radialModulation: boolean;
  modulationOffset: number; // 0-1
}

export interface NoiseSettings {
  enabled: boolean;
  opacity: number;    // 0-1
  premultiply: boolean;
}

export interface ToneMappingSettings {
  enabled: boolean;
  mode: ToneMappingModeValue;
}

export interface GlitchSettings {
  enabled: boolean;
  delayMin: number;     // seconds
  delayMax: number;     // seconds
  durationMin: number;  // seconds
  durationMax: number;  // seconds
  strengthMin: number;  // 0-2
  strengthMax: number;  // 0-2
}

export interface ScanlineSettings {
  enabled: boolean;
  density: number;      // 0-20
  opacity: number;      // 0-1
  scrollSpeed: number;  // 0-5
}

export interface SmaaSettings {
  enabled: boolean;
}

// ---- Combined settings ----

export interface PostprocessingSettings {
  bloom: BloomSettings;
  vignette: VignetteSettings;
  chromaticAberration: ChromaticAberrationSettings;
  noise: NoiseSettings;
  toneMapping: ToneMappingSettings;
  glitch: GlitchSettings;
  scanline: ScanlineSettings;
  smaa: SmaaSettings;
}

// ---- Client version for settings persistence ----

export const CLIENT_VERSION = '1.0.0';

// ---- Default values ----

export const DEFAULT_SETTINGS: PostprocessingSettings = {
  bloom: {
    enabled: true,
    intensity: 2.6,
    luminanceThreshold: 0.75,
    luminanceSmoothing: 0.37,
    mipmapBlur: true,
    radius: 0.72,
  },
  vignette: {
    enabled: true,
    darkness: 0.5,
    offset: 0.5,
  },
  chromaticAberration: {
    enabled: false,
    offsetX: 0.002,
    offsetY: 0.002,
    radialModulation: false,
    modulationOffset: 0.5,
  },
  noise: {
    enabled: false,
    opacity: 0.1,
    premultiply: false,
  },
  toneMapping: {
    enabled: true,
    mode: ToneMappingMode.UNCHARTED2,
  },
  glitch: {
    enabled: false,
    delayMin: 1.5,
    delayMax: 3.5,
    durationMin: 0.1,
    durationMax: 0.3,
    strengthMin: 0.1,
    strengthMax: 0.5,
  },
  scanline: {
    enabled: false,
    density: 1.25,
    opacity: 0.5,
    scrollSpeed: 0,
  },
  smaa: {
    enabled: true,
  },
};

// ---- Validation ----

const EFFECT_KEYS: ReadonlyArray<keyof PostprocessingSettings> = [
  'bloom', 'vignette', 'chromaticAberration', 'noise',
  'toneMapping', 'glitch', 'scanline', 'smaa',
];

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function validateNumber(v: unknown, min: number, max: number): v is number {
  return typeof v === 'number' && !Number.isNaN(v) && v >= min && v <= max;
}

function validateBoolean(v: unknown): v is boolean {
  return typeof v === 'boolean';
}

function validateBloom(v: unknown): v is BloomSettings {
  if (!isObject(v)) return false;
  return validateBoolean(v.enabled)
    && validateNumber(v.intensity, 0, 20)
    && validateNumber(v.luminanceThreshold, 0, 1)
    && validateNumber(v.luminanceSmoothing, 0, 1)
    && validateBoolean(v.mipmapBlur)
    && validateNumber(v.radius, 0, 1);
}

function validateVignette(v: unknown): v is VignetteSettings {
  if (!isObject(v)) return false;
  return validateBoolean(v.enabled)
    && validateNumber(v.darkness, 0, 1)
    && validateNumber(v.offset, 0, 1);
}

function validateChromaticAberration(v: unknown): v is ChromaticAberrationSettings {
  if (!isObject(v)) return false;
  return validateBoolean(v.enabled)
    && validateNumber(v.offsetX, 0, 0.1)
    && validateNumber(v.offsetY, 0, 0.1)
    && validateBoolean(v.radialModulation)
    && validateNumber(v.modulationOffset, 0, 1);
}

function validateNoise(v: unknown): v is NoiseSettings {
  if (!isObject(v)) return false;
  return validateBoolean(v.enabled)
    && validateNumber(v.opacity, 0, 1)
    && validateBoolean(v.premultiply);
}

function validateToneMapping(v: unknown): v is ToneMappingSettings {
  if (!isObject(v)) return false;
  const validModes = [0, 1, 2, 4];
  return validateBoolean(v.enabled)
    && typeof v.mode === 'number' && validModes.includes(v.mode);
}

function validateGlitch(v: unknown): v is GlitchSettings {
  if (!isObject(v)) return false;
  return validateBoolean(v.enabled)
    && validateNumber(v.delayMin, 0, 60)
    && validateNumber(v.delayMax, 0, 60)
    && validateNumber(v.durationMin, 0, 10)
    && validateNumber(v.durationMax, 0, 10)
    && validateNumber(v.strengthMin, 0, 2)
    && validateNumber(v.strengthMax, 0, 2);
}

function validateScanline(v: unknown): v is ScanlineSettings {
  if (!isObject(v)) return false;
  return validateBoolean(v.enabled)
    && validateNumber(v.density, 0, 20)
    && validateNumber(v.opacity, 0, 1)
    && validateNumber(v.scrollSpeed, 0, 5);
}

function validateSmaa(v: unknown): v is SmaaSettings {
  if (!isObject(v)) return false;
  return validateBoolean(v.enabled);
}

const VALIDATORS: Record<keyof PostprocessingSettings, (v: unknown) => boolean> = {
  bloom: validateBloom,
  vignette: validateVignette,
  chromaticAberration: validateChromaticAberration,
  noise: validateNoise,
  toneMapping: validateToneMapping,
  glitch: validateGlitch,
  scanline: validateScanline,
  smaa: validateSmaa,
};

/**
 * Validate a complete settings object. Returns null on success, error string on failure.
 */
export function validateSettings(data: unknown): string | null {
  if (!isObject(data)) return 'Settings must be an object';
  for (const key of EFFECT_KEYS) {
    if (!(key in data)) return `Missing effect: ${key}`;
    if (!VALIDATORS[key](data[key])) return `Invalid settings for: ${key}`;
  }
  return null;
}

// ---- Store ----

interface PostprocessingState {
  settings: PostprocessingSettings;
  defaults: PostprocessingSettings;

  updateBloom: (partial: Partial<BloomSettings>) => void;
  updateVignette: (partial: Partial<VignetteSettings>) => void;
  updateChromaticAberration: (partial: Partial<ChromaticAberrationSettings>) => void;
  updateNoise: (partial: Partial<NoiseSettings>) => void;
  updateToneMapping: (partial: Partial<ToneMappingSettings>) => void;
  updateGlitch: (partial: Partial<GlitchSettings>) => void;
  updateScanline: (partial: Partial<ScanlineSettings>) => void;
  updateSmaa: (partial: Partial<SmaaSettings>) => void;

  resetBloom: () => void;
  resetVignette: () => void;
  resetChromaticAberration: () => void;
  resetNoise: () => void;
  resetToneMapping: () => void;
  resetGlitch: () => void;
  resetScanline: () => void;
  resetSmaa: () => void;

  resetToDefaults: () => void;
  setDefaults: (defaults: PostprocessingSettings) => void;
  importSettings: (json: string) => string | null; // returns error string or null on success
  exportSettings: () => string;
  applySettings: (settings: PostprocessingSettings) => void;
}

export const usePostprocessingStore = create<PostprocessingState>()(
  persist(
    (set, get) => ({
      settings: { ...DEFAULT_SETTINGS },
      defaults: { ...DEFAULT_SETTINGS },

      updateBloom: (partial) => set((state) => ({
        settings: { ...state.settings, bloom: { ...state.settings.bloom, ...partial } },
      })),
      updateVignette: (partial) => set((state) => ({
        settings: { ...state.settings, vignette: { ...state.settings.vignette, ...partial } },
      })),
      updateChromaticAberration: (partial) => set((state) => ({
        settings: { ...state.settings, chromaticAberration: { ...state.settings.chromaticAberration, ...partial } },
      })),
      updateNoise: (partial) => set((state) => ({
        settings: { ...state.settings, noise: { ...state.settings.noise, ...partial } },
      })),
      updateToneMapping: (partial) => set((state) => ({
        settings: { ...state.settings, toneMapping: { ...state.settings.toneMapping, ...partial } },
      })),
      updateGlitch: (partial) => set((state) => ({
        settings: { ...state.settings, glitch: { ...state.settings.glitch, ...partial } },
      })),
      updateScanline: (partial) => set((state) => ({
        settings: { ...state.settings, scanline: { ...state.settings.scanline, ...partial } },
      })),
      updateSmaa: (partial) => set((state) => ({
        settings: { ...state.settings, smaa: { ...state.settings.smaa, ...partial } },
      })),

      resetBloom: () => set((state) => ({
        settings: { ...state.settings, bloom: { ...state.defaults.bloom } },
      })),
      resetVignette: () => set((state) => ({
        settings: { ...state.settings, vignette: { ...state.defaults.vignette } },
      })),
      resetChromaticAberration: () => set((state) => ({
        settings: { ...state.settings, chromaticAberration: { ...state.defaults.chromaticAberration } },
      })),
      resetNoise: () => set((state) => ({
        settings: { ...state.settings, noise: { ...state.defaults.noise } },
      })),
      resetToneMapping: () => set((state) => ({
        settings: { ...state.settings, toneMapping: { ...state.defaults.toneMapping } },
      })),
      resetGlitch: () => set((state) => ({
        settings: { ...state.settings, glitch: { ...state.defaults.glitch } },
      })),
      resetScanline: () => set((state) => ({
        settings: { ...state.settings, scanline: { ...state.defaults.scanline } },
      })),
      resetSmaa: () => set((state) => ({
        settings: { ...state.settings, smaa: { ...state.defaults.smaa } },
      })),

      resetToDefaults: () => set((state) => ({
        settings: { ...state.defaults },
      })),

      setDefaults: (defaults) => set({ defaults: { ...defaults } }),

      importSettings: (json: string) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(json);
        } catch {
          return 'Invalid JSON';
        }
        const error = validateSettings(parsed);
        if (error) return error;
        set({ settings: parsed as PostprocessingSettings });
        return null;
      },

      exportSettings: () => {
        return JSON.stringify(get().settings, null, 2);
      },

      applySettings: (settings: PostprocessingSettings) => {
        set({ settings: { ...settings } });
      },
    }),
    {
      name: 'warmancer-postprocessing-v3',
      partialize: (state) => ({ settings: state.settings }),
      merge: (persisted, current) => {
        // Fill missing fields from defaults when schema evolves
        const persistedState = persisted as { settings?: Partial<PostprocessingSettings> } | undefined;
        if (!persistedState?.settings) return current;
        const merged = { ...current };
        merged.settings = { ...current.defaults };
        for (const key of EFFECT_KEYS) {
          if (persistedState.settings[key] && VALIDATORS[key](persistedState.settings[key])) {
            (merged.settings as unknown as Record<string, unknown>)[key] = persistedState.settings[key];
          }
        }
        return merged;
      },
    },
  ),
);
